<template>
  <div class="cutsheet__container">

    <div class="cutsheet__info">

      <button 
        class="button is-link is-fullwidth mb-5"
        :class="{ 'is-loading': saving }" 
        :disabled="!valid || saving"
        @click.stop="saveCutSheet()">
        <span class="icon is-medium">
          <i class="fas fa-save"></i>
        </span>
        <span>              
          Save Cut Sheet
        </span>
      </button>

      <v-form :valid.sync="valid">

        <div class="field">
          <label class="label">Project*</label>
          <search-autocomplete
            v-model="cutSheet.project_id">
          </search-autocomplete>
        </div>

        <div class="field">
          <label class="label">Tag*</label>
          <v-text-field 
            required
            placeholder="Enter Tag"
            v-model="cutSheet.item">
          </v-text-field>
        </div>

        <div class="field">
          <label class="label">Expected Delivery Date*</label>
          <v-datepicker
            required
            v-model="cutSheet.expected_delivery_date">
          </v-datepicker>
        </div>

        <div class="field">
          <label class="label">
            Product*
          </label>
          <span
            class="has-text-grey" 
            v-if="!cutSheet.project_id">
            Select a project to see slabs available for this project
          </span>
          <template v-else>
            <v-select
              v-if="productionSheets.length > 0"
              :options="productionSheetItems"
              :item-text="v => v.slab_product_master.name"
              item-value="id"
              v-model="cutSheet.production_sheet_item_id"
              @input="cutSheet.input_stocking_unit_id = 
                productionSheetItems.filter(d => d.id == $event)[0].slab_stocking_unit_id"
            />
            <span v-else class="has-text-grey-light">
              No production sheets available for this project
            </span>
          </template>

          <div 
            class="mt-2"
            v-if="cutSheet.input_stocking_unit_id">
            <div class="help">
              Min Product Dimensions:
            </div>
            <div>
              {{ minLwt(inputSlab.slab_product_master) }}
            </div>

            <div class="help">
              Max Product Dimensions:
            </div>
            <div>
              {{ maxLwt(inputSlab.slab_product_master) }}
            </div>
          </div>

        </div>

      </v-form>

      <hr>

      <div>
        <a 
          @click="managingSteps = true">
          Manage Cut Sheet Steps
        </a>
      </div>

    </div>

    <div class="cutsheet__parts">

      <div class="level cutsheet__controls">

        <div class="level-left buttons mb-0">
          <a class="button is-primary" @click="addRow()">
            Add a Part
          </a>
        </div>

        <div class="level-right buttons mb-0">
          <a class="button" 
            v-if="itemsSelected.length > 0"
            @click="deleteSelected()">
            <span class="icon is-medium">
              <i class="fas fa-trash has-text-danger"></i>
            </span>
            <span>
              Delete {{ itemsSelected.length }} Part<span v-if="itemsSelected.length > 0">s</span>
            </span>
          </a>
        </div>

      </div>

      <table class="table is-hoverable is-fullwidth sticky-header has-smallish-font">

        <thead>
          <tr class="has-text-centered">
            <th class="part__selector">
              <label class="checkbox" v-if="sortIndexes.length > 0">
                <input
                  type="checkbox"
                  v-model="allSelected">
              </label>
            </th>
            <th 
              v-for="(col, i) in colTemplate"
              :class="col.cellClass" 
              :key="col.label + '-' + i">
              {{ col.label }}
            </th>
            <th 
              class="is-capitalized"
              v-for="step in cutSheetSteps" 
              :key="step.id">
              {{ step.name }}
            </th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr 
            class="v-centered has-text-centered"
            v-for="(row, rowIndex) in cutSheet.parts" 
            :key="row.id">

            <td class="part__selector">
              <label class="checkbox">
                <input
                  type="checkbox"
                  :checked="itemsSelected.indexOf(row.id) != -1"
                  @input="selectItem(row.id)">
              </label>
            </td>

            <td class="border--after">
              <v-content-editable
                class="text--nowrap"
                v-model="row.part_no">
              </v-content-editable>
            </td>

            <td>
              <v-content-editable
                class="text--nowrap"
                v-model="row.width_in_str"
                @error-update="updateError($event, `${rowIndex}-width`)"
                hide-details
                :rules="[
                  inchesValidator,
                  testMinMaxWidth
                ]">
              </v-content-editable>
            </td>

            <td>
              <v-content-editable
                class="text--nowrap"
                v-model="row.length_in_str"
                @error-update="updateError($event, `${rowIndex}-length`)"
                hide-details
                :rules="[
                  inchesValidator,
                  testMinMaxLength
                ]">
              </v-content-editable>
            </td>

            <td class="border--before border--after">
              <v-content-editable
                class="text--nowrap"
                v-model="row.quantity"
                @error-update="updateError($event, `${rowIndex}-quantity`)"
                hide-details
                :rules="[v => !isNaN(v) || 'Quantity must be an integer']">
              </v-content-editable>
            </td>

            <td class="border--after">
              <div class="has-text-centered flex flex--align-center 
                flex--justify-center">
                <v-file-upload
                  class="is-small mr-0"
                  placeholder="choose file"
                  file-type-validator=".*"
                  v-model="row.attachment">
                </v-file-upload>
              </div>
            </td>

            <td>
              <v-checkbox
                v-model="row.requires_assembly">
              </v-checkbox>
            </td>

            <!-- cut sheet steps -->
            <td 
              v-for="step in row.steps" 
              :key="step.name + '-' + step.id">
              <v-checkbox 
                v-model="step.is_required">
              </v-checkbox>
            </td>

            <td>
              <div class="buttons buttons-right flex--nowrap">
                <a
                  v-if="rowIndex != 0"
                  @click="moveUp(rowIndex, row.sort_ix)"
                  class="button is-small">
                  <span class="icon is-medium">
                    <i class="fas fa-chevron-up has-text-grey">
                    </i>
                  </span>
                </a>
                <a
                  v-if="rowIndex + 1 != cutSheet.parts.length"
                  @click="moveDown(rowIndex, row.sort_ix)"
                  class="button is-small">
                  <span class="icon is-medium">
                    <i class="fas fa-chevron-down has-text-grey">
                    </i>
                  </span>
                </a>
                <a
                  class="button is-small"
                  @click="copyItem(row, rowIndex)">
                  <span class="icon is-medium">
                    <i class="fas fa-copy has-text-grey">
                    </i>
                  </span>
                </a>

                <a
                  class="button is-small"
                  @click="deleteItem(rowIndex)">
                  <span class="icon is-medium">
                    <i class="fas fa-trash has-text-grey">
                    </i>
                  </span>
                </a>
              </div>
            </td>
          </tr>
        </tbody>

      </table>
      
      <v-fade-transition mode="out-in">
        <article 
          v-if="uniqueErrors.length > 0" 
          class="message is-danger is-fullwidth">
          <div class="message-body py-3">
            <span class="help">
              Note the following errors:
            </span>
            <li v-for="(error, i) in uniqueErrors" :key="i">
              {{ error }}
            </li>
          </div>
        </article>
      </v-fade-transition>

    </div>

    <manage-steps-modal v-model="managingSteps"/>

  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import VContentEditable from '@/components/base/VContentEditable'
import VSelect from '@/components/base/VSelect'
import VFileUpload from '@/components/base/VFileUpload'
import VDatepicker from '@/components/base/VDatepicker'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import ManageStepsModal from '@/components/cutsheet/ManageStepsModal'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'

import helpers from '@/helpers'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VTextField, VCheckbox, VSelect, ManageStepsModal, VDatepicker,
    VContentEditable, VFileUpload, VForm, SearchAutocomplete, VFadeTransition
  },
  data() {
    return {
      colTemplate: [
        { 
          label: 'Part #', field: 'part_no', component: 'VContentEditable', 
          default: null, cellClass: 'border--after', bind: { class: 'text--nowrap' } 
        },
        { 
          label: 'W"', 
          field: 'width_in_str', 
          component: 'VContentEditable', 
          default: null 
        },
        { 
          label: 'L"', 
          field: 'length_in_str', 
          component: 'VContentEditable', 
          default: null 
        },
        { 
          label: 'Quantity', field: 'quantity', component: 'VContentEditable', 
          default: null, cellClass: 'border--before border--after' 
        },
        { 
          label: 'Shop Ticket', field: 'attachment', component: 'VFileUpload', 
          default: { file: null, name: null, url: null }, 
          bind: { class: 'is-small', placeholder: 'choose file', 'file-type-validator': '.*' } ,
          cellClass: 'border--after'
        },
        { 
          label: 'Assembly', field: 'requires_assembly', component: 'VCheckbox', 
          default: false, 
        }
      ],
      allSelected: false,
      itemsSelected: [],
      defaultRows: 1,
      cutSheet: {
        project_id: null,
        item: '',
        input_stocking_unit_id: null,
        parts: [],
        thick_in_str: null
      },
      managingSteps: false,
      managingProducts: false,
      valid: false,
      saving: false,
      inchesValidator: (v) => {
        const pattern = /^([0-9]+\s*)?(([0-9]+)(?:\/)([1-9][0-9]*))?$/
        return pattern.test(v) || 'Invalid format'
      },
      errors: {},
      parseFraction: helpers.parseFraction
    }
  },
  computed: {
    ...mapState("cutSheets", ["cutSheetSteps"]),
    ...mapState("productionSheets", ["productionSheets"]),
    template() {
      let obj = {};
      this.colTemplate.forEach(col => {
        obj[col.field] = col.default;
      });

      return obj
    },
    ids() {
      return this.cutSheet.parts.map(d => d.id);
    },
    sortIndexes() {
      return this.cutSheet.parts.map(d => d.sort_ix);
    },
    productionSheetItems() {
      return this.productionSheets.map(d => d.items).flat(1);
    },
    inputSlab() {
      return this.productionSheetItems.filter(
        d => d.slab_stocking_unit_id == this.cutSheet.input_stocking_unit_id
      )[0];
    },
    uniqueErrors() {
      return [...new Set(Object.values(this.errors))].flat(1);
    },
    testMinMaxWidth({ inputSlab, parseFraction }) {
      return (v) => {
        if (!inputSlab) {
          return 'Please select an input slab first'
        } else if (!inputSlab.slab_product_master.min_width_in_str) {
          return true
        } else {
          let frac = parseFraction(v);
          let passMin = frac >= inputSlab.slab_product_master.min_width_in_str
          let passMax = frac <= inputSlab.slab_product_master.max_width_in_str
          if (!passMin || !passMax) {
            return 'Incorrect Width'
          } else {
            return true
          }
        }
      }
    },
    testMinMaxLength({ inputSlab, parseFraction }) {
      return (v) => {
        if (!inputSlab) {
          return 'Please select an input slab first'
        } else if (!inputSlab.slab_product_master.min_length_in_str) {
          return true
        } else {
          let frac = parseFraction(v);
          let passMin = frac >= inputSlab.slab_product_master.min_length_in_str
          let passMax = frac <= inputSlab.slab_product_master.max_length_in_str
          if (!passMin || !passMax) {
            return 'Incorrect Length'
          } else {
            return true
          }
        }
      }
    }
  },
  watch: {
    allSelected(state) {
      const { itemsSelected } = this;
      if (state) {
        this.ids.forEach(id => {
          const index = itemsSelected.indexOf(id);
          if (index == -1) {
            itemsSelected.push(id);
          }
        })
      } else {
        itemsSelected.splice(0, itemsSelected.length);
      }
    },
    'cutSheet.project_id'(id) {
      this.getProductionSheets({ project_id: id });
    }
  },
  created() {
    if (this.cutSheetSteps.length == 0) {
      this.getSteps()
        .then(() => this.initialize());
    } else {
      this.initialize();
    }
  },
  methods: {
    ...mapActions("cutSheets", ["getSteps"]),
    ...mapActions("productionSheets", ["getProductionSheets"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      for (let i=1; i <= this.defaultRows; i++) {
        this.addRow();
      }
    },
    updateError(v, id) {
      this.$set(this.errors, id, v);
    },
    minLwt(product) {
      if (!product.min_length_in_str && !product.min_width_in_str && !product.min_thick_in_str) {
        return false
      } else {
        return `L${product.min_length_in_str}" x W${product.min_width_in_str}" x T${product.min_thick_in_str}"`
      }
    },
    maxLwt(product) {
      if (!product.max_length_in_str && !product.max_width_in_str && !product.max_thick_in_str) {
        return false
      } else {
        return `L${product.max_length_in_str}" x W${product.max_width_in_str}" x T${product.max_thick_in_str}"`
      }  
    },
    addRow() {
      let newRow = Object.assign({}, this.template);
      newRow.steps = JSON.parse(JSON.stringify(this.cutSheetSteps));
      newRow.steps.forEach(step => {
        step.is_required = step.required_by_default ? true : false;
      });
      newRow.id = this.ids.length == 0 ? 1 : Math.max(...this.ids) + 1;
      newRow.sort_ix = this.sortIndexes.length == 0 ? 1 : Math.max(...this.sortIndexes) + 1;

      this.cutSheet.parts.push(newRow);
    },
    copyItem(row, rowIndex) {
      let file = row.attachment;
      let newRow = JSON.parse(JSON.stringify(row));
      newRow.id = this.ids.length == 0 ? 1 : Math.max(...this.ids) + 1;
      newRow.sort_ix = newRow.sort_ix = this.sortIndexes.length == 0 ? 1 : Math.max(...this.sortIndexes) + 1;
      newRow.attachment = Object.assign({}, file);

      this.cutSheet.parts.splice(rowIndex + 1, 0, newRow);
      this.cutSheet.parts.forEach((part, i) => {
        part.sort_ix = i;
      });
    },
    moveUp(rowIndex, sort_ix) {
      let prev = this.cutSheet.parts[rowIndex - 1].sort_ix;
      this.cutSheet.parts[rowIndex - 1].sort_ix = sort_ix;
      this.cutSheet.parts[rowIndex].sort_ix = prev;
      this.sort();
    },
    moveDown(rowIndex, sort_ix) {
      let next = this.cutSheet.parts[rowIndex + 1].sort_ix;
      this.cutSheet.parts[rowIndex + 1].sort_ix = sort_ix;
      this.cutSheet.parts[rowIndex].sort_ix = next;
      this.sort();
    },
    sort() {
      this.cutSheet.parts.sort((a, b) => a.sort_ix - b.sort_ix);
    },
    selectItem(i) {
      let index = this.itemsSelected.indexOf(i);
      if (index == -1) {
        this.itemsSelected.push(i);
      } else {
        this.itemsSelected.splice(index, 1);
        if (this.itemsSelected.length == 0) {
          this.allSelected = false;
        }
      }
    },
    deleteItem(i) {
      this.cutSheet.parts.splice(i, 1);
    },
    deleteSelected() {
      this.itemsSelected.forEach(item => {
        this.cutSheet.parts.forEach((part, i) => {
          if (part.id == item) {
            this.cutSheet.parts.splice(i, 1);
          }
        });
      });
      this.itemsSelected.splice(0, this.itemsSelected.length);
    },  
    changeFile(v) {
      this.attachment = v;
      this.$http.cutsheet.attach();
    },
    saveCutSheet() {
      this.saving = true;
      let cutSheetCopy = Object.assign({}, this.cutSheet);
      let parts = [];

      cutSheetCopy.parts.forEach(part => {
        part.steps = part.steps.filter(
          d => d.is_required == true
        ).map(d => { return { id: d.id }});

        if (!part.part_no && !part.length_in_str && !part.width_in_str && !part.quantity) {
          return
        } else {
          parts.push(Object.assign({}, part));
        }
      })

      cutSheetCopy.parts = [];

      this.$http.cutsheets.post(cutSheetCopy)
        .then(resp => {
          if (parts.length > 0) {
            this.saveCutSheetSuccess(resp.data, parts);
          } else {
            this.saving = false;
            this.goToEdit(resp.data);
            this.notifySavedSuccess();
          }
        });
    },
    saveCutSheetSuccess(cutSheet, parts) {
      let savedCutSheet = cutSheet;

      parts.forEach((part, i) => {
        // Copy attachment
        let attachment = Object.assign({}, part.attachment);
        part.attachment = null;

        this.$http.cutsheetParts.post(part, { cutsheet_id: cutSheet.id })
          .then(resp => {
            if (attachment.file) {
              this.savePartAttachment(resp.data.id, attachment.file)
                .then(resp => {
                  savedCutSheet.parts.push(resp.data);
                  if (i+1 == parts.length) {
                    this.saving = false;
                    this.goToEdit(savedCutSheet);
                    this.notifySavedSuccess();
                  }
                });
            } else {
              savedCutSheet.parts.push(resp.data);
              if (i+1 == parts.length) {
                this.saving = false;
                this.goToEdit(savedCutSheet);
                this.notifySavedSuccess();
              }
            }
          })
      })
    },
    savePartAttachment(partId, attachment) {
      return new Promise((resolve, reject) => {
        let formData = new FormData();
        formData.append('attachment', attachment, attachment.name);
        this.$http.cutsheetPart.addAttachment(partId, formData)
          .then(resp => {
            resolve(resp);
          }).catch(() => {
            reject();
          })
      })
    },
    notifySavedSuccess() {
      this.setNotification({
        type: 'success',
        status: true,
        message: 'Cut sheet successfully created'
      });
    },
    goToEdit(cutSheet) {
      this.$router.push(
        { 
          name: 'CutSheetEditor', 
          params: { cutSheetId: cutSheet.id, cutSheet: cutSheet }
        }
      );
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';

.editable-text {
  min-width: 30px;
}

.cutsheet {
  &__container {
    display: flex;
    flex-direction: row;
  }
  &__info {
    padding: 1.5em;
    width: 15%;
  }
  &__parts {
    padding-right: 1.5em;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    width: 85%;
    overflow-x: auto;
  }
}

.label-and-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .tag {
    cursor: pointer;
    &:hover {
      background: gainsboro;
    }
  }
}

@media print {
  .cutsheet__controls {
    display: none;
  }
  .part__selector {
    display: none;
  }
}

</style>
